import React, { useContext, useEffect, useState } from "react";
import { connect } from "react-redux";
import { debug, hasErrors } from "../../utils";
import * as modalDataService from './modalDataService';
import * as devicePageService from '../deviceService';
import * as validationService from './validationService';
import { showDangerToast, showSuccessToast } from "../../validation/ToastContainer";
import { PopupContext } from "../../index";
import { getConfirmPopup } from "../../view/popup/PopupContainer";
import * as deviceService from "../deviceService";

const STEPS_AMOUNT = 3;

function AddDeviceModalFooter({ modalData, validationErrors, username, disabled, licenseChecked, deviceCount, allowedDevices }) {


    const { setPopup } = useContext(PopupContext);
    const { step } = modalData;

    const [multiDeviceDisable, setMultiDeviceDisable] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    useEffect(() => {
        let ipString = modalData.mngtIpAddress;
        if (ipString && typeof ipString == 'string') {
            // Remove spaces
            ipString = ipString.trim();        
            // If the string contains commas, split and process each part
            if (ipString.includes(',') || ipString.includes('-')) {
                setMultiDeviceDisable(true)
                return;
            }
        }
        setMultiDeviceDisable(false);
    }, [modalData.mngtIpAddress])

    const onNextClick = () => {
        modalDataService.changeStep(step + 1);
    }

    const onPrevClick = () => {
        modalDataService.changeStep(step - 1);
    }

    const showNext = () => {
        return step < STEPS_AMOUNT;
    }

    const showPrew = () => {
        return step > 0;
    }

    const showFinish = () => {
        return step === STEPS_AMOUNT;
    }

    const getNextLabel = () => {
        const nextStep = step + 2;

        if (nextStep <= 4) {
            return `Continue to Step ${nextStep} \u2192`;
        }

        return "Continue";
    }

    const saveDevice = async () => {
        const response = await devicePageService.save({ ...modalData, ownerUsername: username });

        if (!response) {
            showDangerToast('Device could not be added', 'Add Device');
            return false;
        }

        const isFetchResponse = typeof response.json === 'function';
        const payload = isFetchResponse ? await response.json() : response;

        if (isFetchResponse && !response.ok) {
            showDangerToast(payload.message || 'Device could not be added', 'Add Device');
            return false;
        }

        if (payload.id || (isFetchResponse && response.ok)) {
            showSuccessToast('Device added Successfully', 'Add Device');
        }

        await devicePageService.listPageable(0, 10);
        modalDataService.hideModal();
        validationService.cleanState();
        modalDataService.cleanState();
        return true;
    }

    const getDeviceIpList = (ipString) => {
        if (!ipString || typeof ipString !== 'string') return [];

        ipString = ipString.trim();

        if (ipString.includes(',')) {
            return ipString
                .split(',')
                .map(s => s.trim())
                .flatMap(part => getDeviceIpList(part));
        }

        if (ipString.includes('-')) {
            const [startIp, endPart] = ipString.split('-').map(s => s.trim());
            const startOctets = startIp.split('.').map(Number);
            const endOctet = endPart.includes('.')
                ? Number(endPart.split('.').pop())
                : Number(endPart);
            const startOctet = startOctets[3];
            const base = startOctets.slice(0, 3).join('.');
            const ips = [];

            for (let i = startOctet; i <= endOctet; i++) {
                ips.push(`${base}.${i}`);
            }

            return ips;
        }

        return [ipString];
    };

    const getHostNamesForSubmit = () => {
        const ipList = getDeviceIpList(modalData.mngtIpAddress);

        if (ipList.length > 1) {
            return ipList.map((ip, index) => `${modalData.hostName}${index + 1}`);
        }

        return [modalData.hostName];
    };

    const checkDuplicateBeforeSubmit = async () => {
        const allDevices = await deviceService.list();
        const ipList = getDeviceIpList(modalData.mngtIpAddress);
        const hostNames = getHostNamesForSubmit();
        const deviceTypeId = Number(modalData.deviceTypeId);

        const found_ip = allDevices.find(obj =>
            ipList.includes(obj.mngtIpAddress)
            && Number(obj.deviceType && obj.deviceType.id) === deviceTypeId
        );
        const found_name = allDevices.find(obj => hostNames.includes(obj.hostName));

        validationService.updateValidation(!found_ip, 'mngtIpAddress', 'This device IP already exists for this device type');
        validationService.updateValidation(!found_name, 'hostName', 'This device name already exists');

        if (found_name) {
            modalDataService.changeStep(0);
            scrollToFieldError('hostname');
            return false;
        }

        if (found_ip) {
            modalDataService.changeStep(0);
            scrollToFieldError('ip');
            return false;
        }

        return true;
    };

    const onClearClick = () => {
        setPopup(
            getConfirmPopup('All information will be deleted',
                'Clear',
                () => {
                    modalDataService.cleanState();
                    validationService.cleanState();
                }
            ))
    }

    const isButtonDisabled = () => disabled || isSubmitting || hasErrors(validationErrors);

    const isNextButtonDisabled = () => {
        if (step === 0) {
            return isButtonDisabled();
        }

        if (multiDeviceDisable) {
            return false;
        }

        if (validationErrors.fetchConfig === "Not Connected") {
            return false;
        }

        return isButtonDisabled();
    };

    const scrollToFieldError = (fieldId) => {
        window.setTimeout(() => {
            const fieldElement = document.getElementById(fieldId);

            if (fieldElement) {
                fieldElement.scrollIntoView({ behavior: "smooth", block: "center" });
                fieldElement.focus({ preventScroll: true });
            }
        }, 0);
    };

    //Added by Octopus DevTeam Sep 26-2022
    //Check if a device ip or name exists and then saves it if it doesn't exist
    const checkIfDeviceExist = async () => {
        await deviceService.list().then(allDevices => {
            const found_ip = allDevices.find(obj => obj.mngtIpAddress === modalData.mngtIpAddress && obj.deviceType["id"] === modalData.deviceTypeId)

            const found_name = allDevices.find(obj => obj.hostName === modalData.hostName);

            validationService.updateValidation(!found_ip, 'mngtIpAddress', 'This device IP already exists for this device type');
            validationService.updateValidation(!found_name, 'hostName', 'This device name already exists');

            if (!found_ip && !found_name) {
                onNextClick();
                return;
            }

            if (found_name) {
                scrollToFieldError('hostname');
                return;
            }

            if (found_ip) {
                scrollToFieldError('ip');
            }

        });

    }


    return (
        <>
            {licenseChecked && disabled ? (
                <div className="oct_adddevice-modalfooter modal-v2-license-warning">
                    Maximum number of devices reached
                    {allowedDevices > 0 ? ` (${deviceCount}/${allowedDevices})` : ''}
                </div>
            ) : ''}
            <div className="modal-footer modal-v2-footer">
                <div className="modal-v2-footer-note">
                    <span className="modal-v2-footer-note-star">*</span> Required fields
                </div>
                <div className="modal-v2-footer-actions">
                    {showPrew() &&
                        <button type="button"
                            className="btn modal-v2-btn modal-v2-btn-outline"
                            disabled={disabled || isSubmitting}
                            onClick={() => onPrevClick()}>
                            {`\u2190 Back to Step ${step}`}
                        </button>}

                    <button className="btn  modal-v2-btn modal-v2-btn-outline"
                        disabled={isSubmitting}
                        onClick={() => onClearClick()}>Clear Form</button>

                    {/* Add condition on disabled property for the button to by pass the test configuration by Octopus DevTeam december 23,2022 */}
                    {showNext() &&
                        <button type="button"

                            //disabled={isButtonDisabled()}
                            //disabled={validationErrors.fetchConfig === "Not Connected" ? false : isButtonDisabled()}
                            disabled={isNextButtonDisabled()}
                            className="btn modal-v2-btn modal-v2-btn-primary"
                            onClick={() => //onNextClick()
                            {

                                if (step === 0 && hasErrors(validationErrors)) {
                                    return;
                                }

                                //If step2
                                if (step + 2 === 2) {
                                    checkIfDeviceExist()
                                }

                                //if step3 but we have validation errors
                                if ((step + 2) === 3 && hasErrors(validationErrors)) {
                                    if (multiDeviceDisable) {
                                        onNextClick();
                                    }
                                    else {
                                        setPopup(
                                            getConfirmPopup('Warning: connection to device failed. Do you want to proceed?',
                                                'Configuration Error',
                                                () => onNextClick()
                                            )
                                        );
                                    }

                                }
                                //if step3 but there are no validation errors
                                else if ((step + 2) === 3 && !hasErrors(validationErrors)) {
                                    onNextClick();
                                }

                                //if step4
                                if (step + 2 === 4) { onNextClick() }

                            }


                        }>
                            {getNextLabel()}
                        </button>}
                    {/*  */}

                    {showFinish() &&
                        <button type="button"
                            className={`btn modal-v2-btn modal-v2-btn-primary ${isSubmitting ? "modal-v2-btn-loading" : ""}`}
                            disabled={isButtonDisabled()}
                            onClick={async () => {
                                if (isSubmitting) {
                                    return;
                                }

                                setIsSubmitting(true);
                                try {
                                    const canSubmit = await checkDuplicateBeforeSubmit();
                                    if (canSubmit) {
                                        await saveDevice();
                                    }
                                } catch (error) {
                                    showDangerToast('Device could not be added', 'Add Device');
                                } finally {
                                    setIsSubmitting(false);
                                }
                            }
                            }>
                            {isSubmitting && <span className="modal-v2-step2-button-loader" />}
                            <span>{isSubmitting ? "Finishing..." : "Finish"}</span>
                        </button>
                    }
                </div>
            </div>
        </>
    )
}

function mapStateToProps(state) {
    debug("AddDeviceModalFooter mapStateToProps", state);
    return {
        modalData: state.addDeviceModal,
        validationErrors: state.validation,
        username: state.auth.username,
    };
}

export default connect(mapStateToProps)(AddDeviceModalFooter);
